import { ActionType, createAction, createReducer } from 'typesafe-actions';
import { combineReducers } from 'redux';
import { TTintColor, TTintColorWithQuantity, TTintService } from '../components/pdp-select-tint-color/types';

export const SET_IS_TINT_SERVICE_AVAILABLE = createAction(
  'product/SET_IS_TINT_SERVICE_AVAILABLE',
)<boolean>();

export const SET_ACTIVE_TINT_COLOR = createAction(
  'product/SET_ACTIVE_TINT_COLOR',
)<null | TTintColorWithQuantity>();

export const ADD_COLOR_TO_SELECTED_COLORS = createAction('product/ADD_COLOR_TO_SELECTED_COLORS')<
  never | TTintColor
>();

export const DELETE_ACTIVE_COLOR_FROM_SELECTED_COLORS = createAction(
  'product/DELETE_COLOR_FROM_SELECTED_COLORS',
)<never>();

export const UPDATE_TINT_SERVICE_QUANTITY_FOR_ACTIVE_COLOR = createAction(
  'product/UPDATE_TINT_SERVICE_QUANTITY_FOR_ACTIVE_COLOR',
)<number>();

export const START_TINT_SERVICE = createAction('product/START_TINT_SERVICE')<never>();

export const SET_INITIAL_TINT_VALUES = createAction('product/SET_INITIAL_TINT_VALUES')<
  Required<TTintService>[]
>();

export const DELETE_COLOR_FROM_COOKIE = createAction('product/DELETE_COLOR_FROM_COOKIE')<never>();

export const SELECT_COLOR_BUTTON_IS_IN_VIEWPORT = createAction(
  'product/SELECT_COLOR_BUTTON_IS_IN_VIEWPORT',
)<never>();

export const SELECT_COLOR_BUTTON_IS_CLICKED = createAction(
  'product/SELECT_COLOR_BUTTON_IS_CLICKED',
)<never>();

export const TINT_SERVICE_ACTIONS = {
  SET_ACTIVE_TINT_COLOR,
  SET_IS_TINT_SERVICE_AVAILABLE,
  ADD_COLOR_TO_SELECTED_COLORS,
  DELETE_ACTIVE_COLOR_FROM_SELECTED_COLORS,
  UPDATE_TINT_SERVICE_QUANTITY_FOR_ACTIVE_COLOR,
  START_TINT_SERVICE,
  SET_INITIAL_TINT_VALUES,
  DELETE_COLOR_FROM_COOKIE,
  SELECT_COLOR_BUTTON_IS_IN_VIEWPORT,
  SELECT_COLOR_BUTTON_IS_CLICKED,
};

export const isTintServiceAvailable = createReducer<
  boolean,
  ActionType<typeof SET_IS_TINT_SERVICE_AVAILABLE>
>(false).handleAction([SET_IS_TINT_SERVICE_AVAILABLE], (state, { payload }) => payload);

export const tintColors = createReducer<
  {
    activeColor: null | TTintColorWithQuantity;
    selectedColors: never | TTintColorWithQuantity[];
  },
  | ActionType<typeof ADD_COLOR_TO_SELECTED_COLORS>
  | ActionType<typeof SET_ACTIVE_TINT_COLOR>
  | ActionType<typeof DELETE_ACTIVE_COLOR_FROM_SELECTED_COLORS>
  | ActionType<typeof UPDATE_TINT_SERVICE_QUANTITY_FOR_ACTIVE_COLOR>
  | ActionType<typeof SET_INITIAL_TINT_VALUES>
>({
  activeColor: null,
  selectedColors: [],
})
  .handleAction([ADD_COLOR_TO_SELECTED_COLORS], (state, { payload }) => {
    const { selectedColors } = state;

    const colorDataFromSelectedColors = selectedColors.find(
      (item) => item.COLOR_NAME === payload.COLOR_NAME,
    );
    const isNewColor = colorDataFromSelectedColors === undefined;

    if (isNewColor) {
      const colorWithQuantity = { ...payload, serviceQuantity: 0 };

      return {
        selectedColors: [...selectedColors, colorWithQuantity],
        activeColor: colorWithQuantity,
      };
    } else {
      return {
        ...state,
        activeColor: colorDataFromSelectedColors,
      };
    }
  })
  .handleAction([SET_ACTIVE_TINT_COLOR], (state, { payload }) => {
    return {
      ...state,
      activeColor: payload,
    };
  })
  .handleAction([DELETE_ACTIVE_COLOR_FROM_SELECTED_COLORS], (state) => {
    const { selectedColors, activeColor } = state;

    if (activeColor) {
      const filteredColors = selectedColors.filter((item) => item.COLOR_NAME !== activeColor.COLOR_NAME);
      const newActiveColor = filteredColors.length > 0 ? filteredColors[filteredColors.length - 1] : null;
      return {
        activeColor: newActiveColor,
        selectedColors: filteredColors,
      };
    }

    return {
      ...state,
    };
  })
  .handleAction([UPDATE_TINT_SERVICE_QUANTITY_FOR_ACTIVE_COLOR], (state, { payload }) => {
    const { activeColor, selectedColors } = state;

    if (activeColor) {
      const activeColorWithNewQuantity = { ...activeColor, serviceQuantity: payload };

      const selectedColorsWithUpdatedActiveColor = selectedColors.map((item) => {
        if (item.COLOR_NAME === activeColor.COLOR_NAME) {
          return activeColorWithNewQuantity;
        }
        return item;
      });

      return {
        activeColor: activeColorWithNewQuantity,
        selectedColors: selectedColorsWithUpdatedActiveColor,
      };
    }

    return {
      ...state,
    };
  })
  .handleAction([SET_INITIAL_TINT_VALUES], (_, { payload }) => {
    const mappedTintColors = payload.map((item) => {
      return {
        COLOR_NAME: item.parameters.color,
        HEX: item.hex,
        serviceQuantity: item.quantity,
      };
    });

    return {
      activeColor: null,
      selectedColors: mappedTintColors,
    };
  });

export const tintService = combineReducers({
  isTintServiceAvailable,
  tintColors,
});
